import { motion } from 'framer-motion';
import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const galleryImages = [
  {
    id: 1,
    src: 'https://images.unsplash.com/photo-1759330203240-b89ccee8840f?crop=entropy&cs=srgb&fm=jpg&w=800',
    alt: 'Indoor playground ball pit',
    category: 'indoor',
    span: 'col-span-2 row-span-2'
  },
  {
    id: 2,
    src: 'https://images.unsplash.com/photo-1771169204750-3b1b20d98053?crop=entropy&cs=srgb&fm=jpg&w=600',
    alt: 'Outdoor playground',
    category: 'outdoor',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 3,
    src: 'https://images.unsplash.com/photo-1649729018392-3dc7e538289e?crop=entropy&cs=srgb&fm=jpg&w=600',
    alt: 'Candy bar setup',
    category: 'candy',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 4,
    src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&auto=format&fit=crop',
    alt: 'Birthday party celebration',
    category: 'birthday',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 5,
    src: 'https://images.unsplash.com/photo-1694984865944-04e836a0648f?crop=entropy&cs=srgb&fm=jpg&w=600',
    alt: 'Sweets table display',
    category: 'candy',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 6,
    src: 'https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/yoy7ojm7_mascot%26candy_5.jpg',
    alt: 'Our mascot characters',
    category: 'mascot',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 7,
    src: 'https://images.pexels.com/photos/1081204/pexels-photo-1081204.jpeg?auto=compress&cs=tinysrgb&w=800',
    alt: 'Digital learning tablet',
    category: 'stem',
    span: 'col-span-1 row-span-1'
  },
  {
    id: 8,
    src: 'https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/3m2xe9th_mascot%26candy_3.jpg',
    alt: 'Easter Rabbit mascot celebration',
    category: 'mascot',
    span: 'col-span-1 row-span-1'
  }
];

export const GallerySection = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const categories = [
    { id: 'all', label: t('gallery.filters.all') },
    { id: 'indoor', label: t('gallery.filters.indoor') },
    { id: 'outdoor', label: t('gallery.filters.outdoor') },
    { id: 'birthday', label: t('gallery.filters.birthday') },
    { id: 'mascot', label: t('gallery.filters.mascots') },
    { id: 'candy', label: t('gallery.filters.candy') },
    { id: 'stem', label: t('gallery.filters.stem') }
  ];

  const filteredImages = activeCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIdx = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(nextIdx);
    setSelectedImage(filteredImages[nextIdx]);
  };

  const prevImage = () => {
    const prevIdx = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIdx);
    setSelectedImage(filteredImages[prevIdx]);
  };

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-brand-blue/10 text-brand-blue font-heading font-bold text-sm mb-4">
            {t('gallery.badge')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('gallery.title')}
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
            {t('gallery.subtitle')}
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`gallery-filter-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full font-heading font-bold text-sm transition-all ${
                activeCategory === cat.id
                  ? 'bg-brand-orange text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          layout
          className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]"
        >
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className={`${image.span} relative rounded-2xl overflow-hidden cursor-pointer group img-zoom`}
              onClick={() => openLightbox(image, index)}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="font-heading font-bold text-white text-lg">{image.alt}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-5xl p-0 bg-transparent border-none shadow-none">
            <div className="relative">
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {selectedImage && (
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default GallerySection;
